// ./src/components/generic/OdometerDisplay/OdometerDisplay.tsx
// Displays odometer reading with optional anomaly warning

import React from "react";
import { Group, Text, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { formatNumber } from "@/utils/formatHelpers";

import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export interface OdometerDisplayProps {
  /** Odometer value to display */
  value: number | undefined | null;

  /** Distance unit label (e.g., "km", "mi") */
  distanceLabel: string;

  /** Whether this reading has an anomaly (lower than a previous reading) */
  hasAnomaly?: boolean;

  /** Text size variant */
  size?: "xs" | "sm" | "md" | "lg";

  /** Whether to show the distance label */
  showLabel?: boolean;

  /** Custom placeholder for null/undefined values */
  placeholder?: string;

  /** Custom tooltip text for anomaly warning */
  anomalyTooltip?: string;
}

// ============================================
// Component
// ============================================

/**
 * OdometerDisplay - Shows an odometer reading with optional anomaly warning.
 *
 * When an anomaly is detected (reading is lower than a previous reading for
 * the same vehicle), displays a warning icon with tooltip to alert the user
 * of a potential data entry error.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <OdometerDisplay value={125000} distanceLabel="km" />
 *
 * // With anomaly warning
 * <OdometerDisplay
 *   value={120000}
 *   distanceLabel="km"
 *   hasAnomaly={true}
 * />
 *
 * // Different sizes
 * <OdometerDisplay value={125000} distanceLabel="mi" size="lg" />
 * ```
 */
const OdometerDisplayComponent: React.FC<OdometerDisplayProps> = ({
  value,
  distanceLabel,
  hasAnomaly = false,
  size = "sm",
  showLabel = true,
  placeholder = "—",
  anomalyTooltip,
}) => {
  const { t } = useTranslation(["common"]);

  // Handle null/undefined values
  if (value === undefined || value === null) {
    return (
      <Text size={size} c="dimmed">
        {placeholder}
      </Text>
    );
  }

  // Format the value
  const formattedValue = showLabel
    ? `${formatNumber(value, 0)} ${distanceLabel}`
    : formatNumber(value, 0);

  // Determine font weight based on size
  const fontWeight = size === "md" || size === "lg" ? 500 : undefined;

  // No anomaly - simple display
  if (!hasAnomaly) {
    return (
      <Text size={size} fw={fontWeight}>
        {formattedValue}
      </Text>
    );
  }

  // Anomaly detected - show warning
  const tooltipText =
    anomalyTooltip ||
    t("common:validation.odometerAnomaly", {
      defaultValue:
        "Odometer reading is lower than a previous record for this vehicle",
    });

  // Icon size based on text size
  const iconSize = size === "xs" ? 14 : size === "sm" ? 16 : 18;

  return (
    <Tooltip label={tooltipText} withArrow multiline w={220}>
      <Group gap={4} wrap="nowrap" style={{ cursor: "help" }}>
        <AppIcon iconName="AlertTriangle" size={iconSize} color="orange" />
        <Text size={size} fw={fontWeight} c="orange.7">
          {formattedValue}
        </Text>
      </Group>
    </Tooltip>
  );
};

export const OdometerDisplay = React.memo(OdometerDisplayComponent);